import { FC, useMemo, useState } from 'react';

import { Formik } from 'formik';
import { type FormikHelpers } from 'formik';
import {
  ADDRESS_FORM,
  ADDRESS_SCHEMA,
} from 'modules/account/contact/forms/schema';
import { BsFillPencilFill } from 'react-icons/bs';
import { cn } from 'utils/helpers';
import useModal from 'ui/partials/Modal/useModal';

import QuickEditAddressForm from './QuickEditAddressForm';

interface PROPS {
  title: string;
  isNumber?: boolean;
  onSubmit: (val: any, onSuccess: () => void) => void;
  isLoading?: boolean;
  initialValues: ADDRESS_FORM;
  isCompany?: boolean;
  disabled?: boolean;
}

const QuickEditAddress: FC<PROPS> = ({
  title,
  isLoading,
  initialValues,
  isCompany,
  onSubmit: _onSubmit,
  disabled,
}) => {
  const { hideModal, showModal } = useModal();
  const [showEdit, setShowEdit] = useState(false);

  const onSubmit = (
    values: ADDRESS_FORM,
    { setSubmitting }: FormikHelpers<ADDRESS_FORM>,
  ) => {
    _onSubmit(values, () => {
      hideModal();
      setSubmitting(false);
    });
  };

  const handleOpenEditModal = () => {
    showModal({
      title: 'Edit Address',
      content: (
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={ADDRESS_SCHEMA}
        >
          {() => <QuickEditAddressForm isCompany={isCompany} />}
        </Formik>
      ),
    });
  };

  const label = useMemo(() => {
    const {
      country,
      state,
      city,
      streetAddress1,
      streetAddress2,
      zipcode,
      postalCode,
    } = initialValues;

    if (
      !country &&
      !state &&
      !city &&
      !streetAddress1 &&
      !streetAddress2 &&
      !zipcode &&
      !isCompany
    ) {
      return `no ${title} yet`;
    }

    if (isCompany && !postalCode && !state && !city) {
      return `no ${title} yet`;
    }

    if (isCompany) {
      // eslint-disable-next-line max-len
      return `${state ? `${state}, ` : ''}${city ? `${city}, ` : ''}${postalCode ? `${postalCode}` : ''}`;
    }

    return (
      // eslint-disable-next-line max-len
      `${country ? `${country}, ` : ''}${state ? `${state}, ` : ''}${city ? `${city}, ` : ''}${streetAddress1 ? `${streetAddress1}, ` : ''}${streetAddress2 ? `${streetAddress2}, ` : ''}${zipcode ? `${zipcode}` : ''}`
    );
  }, [initialValues, isCompany, title]);

  const noValue = useMemo(() => {
    const {
      country,
      state,
      city,
      streetAddress1,
      streetAddress2,
      zipcode,
      postalCode,
    } = initialValues;

    if (
      !country &&
      !state &&
      !city &&
      !streetAddress1 &&
      !streetAddress2 &&
      !zipcode &&
      !isCompany
    )
      return true;

    if (isCompany && !postalCode && !state && !city) return true;

    return false;
  }, [initialValues, isCompany]);

  return (
    <div className="border-b-[0.5px] border-b-separator-primary py-[14px]">
      <p className={cn('text-sm secondary text-content')}>{title}</p>

      <div
        className="flex items-center gap-1"
        onMouseEnter={() => setShowEdit(true)}
        onMouseLeave={() => setShowEdit(false)}
      >
        <p
          className={cn(
            'text-sm content primary',
            showEdit && 'truncate',
            !noValue ? '' : 'text-disabled italic lowercase',
          )}
        >
          {label}
        </p>
        <button
          disabled={isLoading}
          type="button"
          className={cn(showEdit && !disabled ? 'block' : 'hidden')}
          onClick={handleOpenEditModal}
        >
          <BsFillPencilFill className="w-3 h-3 text-secondary-base" />
        </button>
      </div>
    </div>
  );
};

export default QuickEditAddress;
